<template>
  <div
    class="tablist"
    role="tablist"
    @keydown.left.prevent="navigate(-1, null)"
    @keydown.right.prevent="navigate(1, null)"
    @keydown.home.prevent="navigate(null, 0)"
    @keydown.end.prevent="navigate(null, tabs.length - 1)"
  >
    <DropdownSelect
      v-if="isMobile && !props.mobileTabs"
      dropdownLabel="Select label"
      :optionsArray="selectOptions"
      :valueIndexOnMount="selectedIndex"
      classes="tablist__select"
      icon-red="true"
      @update:value="showPanel"
    />
    <div
      v-else
      ref="tabsRef"
      :class="{ tablist__tabs: true, overflowing: overflowX > 0 }"
    >
      <DropdownSelect
        v-if="overflowX > 0 && overflowSelectOptions.length > 0"
        ref="overflowSelectRef"
        dropdownLabel="More"
        :optionsArray="overflowSelectOptions"
        :valueIndexOnMount="-1"
        classes="tablist__more"
        icon-red="true"
        @update:value="showPanel"
      />
      <button
        v-for="(tab, index) in tablistOptions"
        :id="`tab-${index}`"
        :key="index"
        class="tablist__tab"
        role="tab"
        :aria-selected="selectedIndex === index"
        :tabindex="selectedIndex === index ? '0' : '-1'"
        @click="
          selectedIndex = index;
          showPanel(index);
          resetMoreDropdown();
        "
        @keydown.enter="
          selectedIndex = index;
          showPanel(index);
          resetMoreDropdown();
        "
      >
        <slot :name="tab"></slot>
      </button>
    </div>
    <div
      role="tabpanel"
      :aria-labelledby="`tab-${selectedIndex}`"
      :aria-hidden="false"
      tabindex="0"
      class="tablist__panel"
    >
      <slot :name="tabs[selectedIndex] + '-panel'"></slot>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, useSlots, onUnmounted } from 'vue';
import DropdownSelect from '../DropdownSelect/DropdownSelect.vue';
import { useIsMobile } from '../../hooks/useIsMobile';

const slots = useSlots();
const tabs = Object.keys(slots).filter(
  (entry) => entry.indexOf('-panel') === -1,
);
const tablistOptions = ref(tabs);
const selectedIndex = ref(
  props.valueIndexOnMount ? parseInt(props.valueIndexOnMount) : 0,
);
const selectOptions = ref([]);
const overflowSelectOptions = ref([]);
const isMobile = useIsMobile();
const overflowX = ref(0);
const tabsRef = ref();
const overflowSelectRef = ref(null);
const checkingOverflow = ref(false);

const props = defineProps({
  mobileTabs: {
    type: Boolean,
    default: false,
  },
  valueIndexOnMount: {
    type: String,
    default: '0',
  },
});

const createSelectOptions = () => {
  const Arr = [];
  for (let i = 0; i < tabs.length; i++) {
    const newObj = { label: slots[tabs[i]]()[0].children, value: i };
    Arr.push(newObj);
  }
  selectOptions.value = Arr;
};

const navigate = (direction, index) => {
  if (index !== null) {
    selectedIndex.value = index;
    showPanel(index);
    return;
  }
  let newSelectedIndex = selectedIndex.value + direction;
  if (selectedIndex.value + direction < 0) {
    newSelectedIndex = tabs.length - 1;
  } else if (selectedIndex.value + direction > tabs.length - 1) {
    newSelectedIndex = 0;
  }
  selectedIndex.value = newSelectedIndex;
  showPanel(newSelectedIndex);
};

const checkIfOverFlowingHandler = () => {
  const element = tabsRef?.value;

  if (element) {
    const elementRect = element.getBoundingClientRect();
    if (element.scrollWidth > elementRect.width) {
      return element.scrollWidth - elementRect.width;
    } else {
      return 0;
    }
  }
};

const getOverflowingChildrenHandler = () => {
  const element = tabsRef?.value;
  const overflowingChildren = [];
  if (element && overflowX.value > 0) {
    const elementRect = element.getBoundingClientRect();
    const children = element.children;
    for (let i = 0; i < children.length; i++) {
      if (children[i].getBoundingClientRect().right > elementRect.right - 120) {
        overflowingChildren.push(i);
      }
    }
  }
  return overflowingChildren;
};

const checkOverflow = () => {
  // If its MobileMenu, just ignore
  if (isMobile.value) {
    return;
  }
  checkingOverflow.value = true;
  tablistOptions.value = tabs;
  resetMoreDropdown();

  // Check if its overflowing
  overflowX.value = checkIfOverFlowingHandler();

  // Get a collection of the overflowing children indexs
  const overflowingChildren = getOverflowingChildrenHandler();

  // Remove items from the tbas list and add them to the more dropdown
  if (overflowingChildren.length > 0) {
    const temp = [...tabs].splice(0, tabs.length - overflowingChildren.length);
    const Arr = [];
    for (const i of overflowingChildren) {
      const newObj = {
        label: slots[tabs[i]]()[0].children,
        value: i,
      };
      Arr.push(newObj);
    }
    tablistOptions.value = temp;
    overflowSelectOptions.value = Arr;
  }
};

onMounted(() => {
  checkOverflow();
  createSelectOptions();
  window.addEventListener('resize', checkOverflow);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkOverflow);
});

const showPanel = (index) => {
  const newIndex =
    typeof index === 'object' ? parseInt(index?.value) : parseInt(index);
  selectedIndex.value = newIndex;
  tabs.forEach((tab, i) => {
    const panel = document.getElementById(`tab-${i}-panel`);
    if (panel) {
      panel.setAttribute('aria-hidden', i !== newIndex);
    }
  });
};

const resetMoreDropdown = () => {
  overflowSelectRef.value?.resetHandler();
};
</script>
